import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Card from '../deal/Card';
import IntegratedSearchBar from '../deal/IntegratedSearchBar';
import { transformAmazonImageUrl, IMAGE_SIZE } from '../../utils/imageUrlTransformer';
import { getDealPricing, formatPrice } from '../../utils/dealPricing';
import { categoryPath } from '../../utils/categorySlug';
import useCategoryPages from '../../hooks/useCategoryPages';
import PromoCodeModal from '../common/PromoCodeModal';
import AffiliateDisclosure from '../common/AffiliateDisclosure';
import {
  trackProductView,
  trackShopButtonClick,
  trackRelatedDealClick,
  trackPageExit,
  useScrollTracking,
  useTimeTracking,
} from '../../utils/analytics';
import { initTrafficSource, transformAffiliateUrl, getTrafficSource } from '../../utils/trafficSource';

function Product() {
  const [content, setContent] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [relatedDeals, setRelatedDeals] = useState([]);
  const [hasConverted, setHasConverted] = useState(false);
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { categoryPages } = useCategoryPages();

  // Search handler — navigates to home page with search query
  const handleSearch = (query) => {
    if (query && query.trim()) {
      navigate(`/?search=${encodeURIComponent(query.trim())}`);
    }
  };

  // Prefer the category's own landing page; fall back to the homepage filter
  const handleCategoryChange = (category) => {
    navigate(categoryPath(category, categoryPages) || `/?category=${encodeURIComponent(category)}`);
  };

  // Analytics tracking hooks
  const timeTrackingRef = useRef(null);
  const scrollCleanupRef = useRef(null);
  // The beforeunload handler is registered once per deal, so it must read the
  // latest content/conversion state through refs rather than stale closures.
  const contentRef = useRef(content);
  const hasConvertedRef = useRef(hasConverted);
  contentRef.current = content;
  hasConvertedRef.current = hasConverted;

  const handleShopClick = () => {
    // Track conversion with traffic source
    const timeSpent = timeTrackingRef.current ? timeTrackingRef.current.getTimeSpent() : 0;
    trackShopButtonClick(content, timeSpent, getTrafficSource());
    setHasConverted(true);
  };

  // Generate dynamic SEO content based on product data
  const generateProductSEO = () => {
    if (!content) {
      return {
        title: 'Loading Deal... | Lite Pocket Deals',
        description: 'Loading amazing deal details from Lite Pocket Deals.',
        keywords: 'deals, discounts, amazon deals, promo codes',
      };
    }

    const { percentOff: discount, salePrice, originalPrice: listPrice, promoCode } = getDealPricing(content);
    const currentPrice = salePrice !== null ? salePrice.toFixed(2) : null;
    const originalPrice = listPrice !== null ? listPrice.toFixed(2) : null;

    // Extract category from title
    const title = content.title.toLowerCase();
    let category = 'products';
    if (title.includes('electronic') || title.includes('tech') || title.includes('gadget')) category = 'electronics';
    else if (title.includes('home') || title.includes('kitchen') || title.includes('house')) category = 'home & kitchen';
    else if (title.includes('fashion') || title.includes('clothing') || title.includes('apparel')) category = 'fashion';
    else if (title.includes('book') || title.includes('read')) category = 'books';
    else if (title.includes('toy') || title.includes('game') || title.includes('play')) category = 'toys & games';
    else if (title.includes('beauty') || title.includes('cosmetic')) category = 'beauty';
    else if (title.includes('sport') || title.includes('fitness') || title.includes('exercise')) category = 'sports';

    // Create SEO-optimized title
    let seoTitle = content.title;
    if (discount) {
      seoTitle = `${content.title} - ${discount}% Off`;
    }
    if (promoCode) {
      seoTitle += ' with Promo Code';
    }
    seoTitle += ' | Lite Pocket Deals';

    // Create description
    let description = `Get ${content.title}`;
    if (discount && currentPrice) {
      description += ` for just $${currentPrice}`;
      if (originalPrice && Number(originalPrice) > Number(currentPrice)) {
        description += ` (was $${originalPrice})`;
      }
      description += ` - ${discount}% off`;
    }
    if (promoCode) {
      description += ` with promo code ${promoCode}`;
    }
    description += `. Save money on ${category} with verified deals and discounts from Lite Pocket Deals.`;

    // Create keywords
    const productKeywords = content.title.split(' ').slice(0, 5).join(', ').toLowerCase();
    const keywords = [
      productKeywords,
      `${category} deals`,
      'amazon deals',
      'discount',
      'promo code',
      'savings',
      'best price',
    ];
    if (discount) keywords.push(`${discount}% off`);

    return {
      title: seoTitle,
      description,
      keywords: keywords.join(', '),
    };
  };

  // Initialize tracking hooks at component level
  const timeTracking = useTimeTracking(dealId);
  const scrollCleanup = useScrollTracking(dealId);

  useEffect(() => {
    // Store tracking references
    timeTrackingRef.current = timeTracking;
    scrollCleanupRef.current = scrollCleanup;
  }, [timeTracking, scrollCleanup]);

  useEffect(() => {
    // Initialize traffic source tracking on page load
    initTrafficSource();

    const script = document.createElement('script');
    script.src = 'https://z-na.amazon-adsystem.com/widgets/onejs?MarketPlace=US&adInstanceId=e7b03285-2c77-42cc-b3e6-e1c729b66b0b';
    script.async = true;
    document.body.appendChild(script);

    // Fetch main deal
    axios
      .get(`/api/deals/${dealId}`)
      .then(response => {
        setContent(response.data);
        setLoading(false);

        // Track product page view once content is loaded
        trackProductView(response.data);
      })
      .catch(error => {
        console.log(`Failed while fetching deal: ${error}`);
        // 404 = expired/unknown deal: show the "Deal not found" state, not a crash message
        if (error.response?.status !== 404) setError('Failed to load deal details');
        setLoading(false);
      });

    // Fetch related deals based on display_name
    axios
      .get(`/api/deals/${dealId}/related`)
      .then(response => {
        if (response.data.deals && response.data.deals.length > 0) {
          setRelatedDeals(response.data.deals);
        }
      })
      .catch(error => {
        console.log(`Failed while fetching related deals: ${error}`);
      });

    // Track page exit on beforeunload
    const handleBeforeUnload = () => {
      if (timeTrackingRef.current && contentRef.current) {
        const timeSpent = timeTrackingRef.current.getTimeSpent();
        trackPageExit(dealId, timeSpent, hasConvertedRef.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (timeTrackingRef.current) {
        timeTrackingRef.current.cleanup();
      }

      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
      }
    };
  }, [dealId]);

  if (loading) {
    return (
      <div className="product-loading">
        <div className="loading-spinner"></div>
        <p>Loading deal details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-error">
        <h3>Oops! Something went wrong</h3>
        <p>{error}</p>
        <Link to="/" className="btn btn-primary">Back to Deals</Link>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="product-not-found">
        <h3>Deal not found</h3>
        <p>The deal you&apos;re looking for doesn&apos;t exist or has expired.</p>
        <Link to="/" className="btn btn-primary">Browse All Deals</Link>
      </div>
    );
  }

  // Generate SEO content
  const seoContent = generateProductSEO();
  const pricing = getDealPricing(content);
  const categoryLink = content.category ? categoryPath(content.category, categoryPages) : null;

  return (
    <div className="product-page">
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <meta name="keywords" content={seoContent.keywords} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Lite Pocket Deals" />
        <meta property="og:image" content={content?.image_url ? transformAmazonImageUrl(content.image_url, IMAGE_SIZE.MEDIUM_DETAIL) : `${window.location.origin}/favicon.ico`} />
        {content && (
          <>
            {pricing.salePrice !== null && (
              <meta property="product:price:amount" content={pricing.salePrice.toFixed(2)} />
            )}
            <meta property="product:price:currency" content="USD" />
            {pricing.percentOff > 0 && (
              <meta property="product:sale_price_effective_date" content={new Date().toISOString()} />
            )}
          </>
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoContent.title} />
        <meta name="twitter:description" content={seoContent.description} />
        <meta name="twitter:image" content={content?.image_url ? transformAmazonImageUrl(content.image_url, IMAGE_SIZE.MEDIUM_DETAIL) : `${window.location.origin}/favicon.ico`} />

        {/* Additional SEO tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lite Pocket Deals" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={`https://www.litepocketdeals.com/deals/${dealId}`} />

        {/* Product-specific meta tags */}
        {content && (
          <>
            <meta name="product:availability" content="in stock" />
            <meta name="product:condition" content="new" />
            {pricing.percentOff > 0 && (
              <meta name="product:discount" content={`${pricing.percentOff}%`} />
            )}
          </>
        )}

        {/* Product structured data for Google rich results */}
        {content && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: content.title,
              image: content.image_url ? transformAmazonImageUrl(content.image_url, IMAGE_SIZE.LARGE_DETAIL) : undefined,
              description: seoContent.description,
              ...(content.asin && { sku: content.asin, mpn: content.asin }),
              ...(content.brand && { brand: { '@type': 'Brand', name: content.brand } }),
              ...(content.category && { category: content.category }),
              ...((content.rating || content.review_count) && {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: content.rating || undefined,
                  reviewCount: content.review_count || content.reviewCount || undefined,
                  bestRating: 5,
                  worstRating: 1,
                },
              }),
              offers: {
                '@type': 'Offer',
                url: `https://www.litepocketdeals.com/deals/${dealId}`,
                priceCurrency: 'USD',
                ...(pricing.salePrice !== null && { price: pricing.salePrice.toFixed(2) }),
                ...(pricing.originalPrice !== null && {
                  priceValidUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                }),
                availability: 'https://schema.org/InStock',
                itemCondition: 'https://schema.org/NewCondition',
                seller: {
                  '@type': 'Organization',
                  name: 'Amazon',
                },
              },
            })}
          </script>
        )}
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb-nav">
        <Link to="/">Home</Link>
        <span className="breadcrumb-separator">›</span>
        {/* Link to the deal's category page: internal links are how its ranking builds up */}
        {categoryLink ? <Link to={categoryLink}>{content.category}</Link> : <Link to="/">Deals</Link>}
        <span className="breadcrumb-separator">›</span>
        <span className="current-page">{content.title}</span>
      </nav>

      {/* Search Bar */}
      <IntegratedSearchBar
        onSearch={handleSearch}
        onCategoryChange={handleCategoryChange}
        loading={false}
        hideFilters
      />

      {/* Main Product Section */}
      <div className="product-container">
        <div className="product-main product-main--card-layout">
          <div className="product-card-hero">
            <Card
              priority
              _id={content._id}
              url={content.url}
              title={content.title}
              image_url={content.image_url}
              orig_price={pricing.originalPrice}
              promoPrice={pricing.salePrice}
              promoCode={content.promo_code || content.promoCode}
              percOff={pricing.percentOff}
              tags={content.tags}
              sales_rank_in_browsenode={content.sales_rank_in_browsenode}
              imageOnly
            />
          </div>

          <div className="product-details-section">
            <h1 className="product-title">{content.title}</h1>

            {/* Price Section */}
            <div className="price-section">
              {pricing.promoCode ? (
                <div className="promo-price">
                  {pricing.hasDiscount && (
                    <div className="original-price">
                      <s>{formatPrice(pricing.originalPrice)}</s>
                    </div>
                  )}
                  <div className="current-price">{formatPrice(pricing.salePrice)}</div>
                  {pricing.percentOff > 0 && (
                    <div className="discount-info">
                      <span className="discount-percent">({pricing.percentOff}% off)</span>
                    </div>
                  )}
                </div>
              ) : (
                pricing.hasDiscount && (
                  <div className="price-comparison">
                    <span className="original-price">
                      <s>{formatPrice(pricing.originalPrice)}</s>
                    </span>
                    <span className="current-price">{formatPrice(pricing.salePrice)}</span>
                    <span className="no-code-text">No code required</span>
                  </div>
                )
              )}
            </div>

            {/* Get Deal Button */}
            <div className="get-deal-section">
              <button
                className="primary-cta"
                onClick={() => {
                  const code = content.promo_code || content.promoCode;
                  if (code) {
                    navigator.clipboard.writeText(code);
                    setShowPromoModal(true);
                  } else {
                    handleShopClick();
                    window.open(transformAffiliateUrl(content.url), '_blank');
                  }
                }}
              >
                {(content.promo_code || content.promoCode)
                  ? '📋 Copy Code & Get Deal on Amazon'
                  : '🛒 Get Deal on Amazon'}
              </button>
              <AffiliateDisclosure className="affiliate-disclosure--inline" detail={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Similar Products */}
      {relatedDeals.length > 0 && (
        <div className="related-deals-section">
          <h2>Similar Products</h2>
          <div className="container">
            <div className="row">
              {relatedDeals.map((deal, index) => (
                <div
                  key={deal._id || index}
                  className="col-sm-4 mb-3"
                  onClick={() => trackRelatedDealClick(dealId, deal._id, index)}
                >
                  <Card {...deal} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Copied Modal */}
      {showPromoModal && (
        <PromoCodeModal
          code={content.promo_code || content.promoCode}
          onClose={() => setShowPromoModal(false)}
          onShop={() => {
            setShowPromoModal(false);
            handleShopClick();
            window.open(transformAffiliateUrl(content.url), '_blank');
          }}
        />
      )}

      {/* Email signup: the footer's working EmailSubscription form sits right below */}

      {/* Spacer */}
      <div style={{ height: '40px' }}></div>

      {/* Amazon Widget */}
      <div id="amzn-assoc-ad-e7b03285-2c77-42cc-b3e6-e1c729b66b0b"></div>
    </div>
  );
}

export default Product;
